/**
 * Creative handlers: add sender/text, delete sender/text.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback } from 'react';
import { uniqueKeepOrderStrings } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { filterSenderNamesMax11 } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import type {
  AiTextMetadataPayload,
  CampaignCreativeUpdate,
} from '@/hooks/smswiser-campaign-workspace/types';

export interface UseCreativeHandlersParams {
  creative: { data?: { senderNames?: string[]; smsTexts?: string[] } | null };
  updateCreative: { mutateAsync: (p: CampaignCreativeUpdate) => Promise<unknown> };
  deleteCampaignSender: { mutateAsync: (name: string) => Promise<unknown> };
  deleteCampaignSendersBulk?: { mutateAsync: (names: string[]) => Promise<unknown> };
  deleteCampaignText: { mutateAsync: (text: string) => Promise<unknown> };
  deleteCampaignTextsBulk?: { mutateAsync: (texts: string[]) => Promise<unknown> };
}

export function useCreativeHandlers({
  creative,
  updateCreative,
  deleteCampaignSender,
  deleteCampaignSendersBulk,
  deleteCampaignText,
  deleteCampaignTextsBulk,
}: UseCreativeHandlersParams) {
  const handleAddSender = useCallback(
    async (name: string) => {
      if (name.length > 11) return;
      const next = uniqueKeepOrderStrings([...(creative.data?.senderNames ?? []), name]).slice(0, 50);
      await updateCreative.mutateAsync({ senderNames: filterSenderNamesMax11(next) });
    },
    [creative.data?.senderNames, updateCreative],
  );

  const handleAddText = useCallback(
    async (texts: string[], aiTextMetadata?: AiTextMetadataPayload[]) => {
      const merged = uniqueKeepOrderStrings([...(creative.data?.smsTexts ?? []), ...texts]).slice(0, 50);
      await updateCreative.mutateAsync({ smsTexts: merged, aiTextMetadata });
    },
    [creative.data?.smsTexts, updateCreative],
  );

  const handleDeleteSender = useCallback(
    async (name: string): Promise<void> => {
      await deleteCampaignSender.mutateAsync(name);
    },
    [deleteCampaignSender],
  );

  const handleDeleteSenders = useCallback(
    async (names: string[]): Promise<void> => {
      if (deleteCampaignSendersBulk && names.length > 0)
        await deleteCampaignSendersBulk.mutateAsync(names);
      else for (const n of names) await deleteCampaignSender.mutateAsync(n);
    },
    [deleteCampaignSender, deleteCampaignSendersBulk],
  );

  const handleDeleteText = useCallback(
    async (text: string): Promise<void> => {
      await deleteCampaignText.mutateAsync(text);
    },
    [deleteCampaignText],
  );

  const handleDeleteTextsBulk = useCallback(
    async (texts: string[]): Promise<void> => {
      if (deleteCampaignTextsBulk) await deleteCampaignTextsBulk.mutateAsync(texts);
      else for (const t of texts) await deleteCampaignText.mutateAsync(t);
    },
    [deleteCampaignText, deleteCampaignTextsBulk],
  );

  return {
    handleAddSender,
    handleAddText,
    handleDeleteSender,
    handleDeleteSenders,
    handleDeleteText,
    handleDeleteTextsBulk,
  };
}
